import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormikErrors, useFormik } from 'formik';
import { array, mixed, object, string } from 'yup';
import { toast } from 'react-toastify';
import { ErrorText, Input, Label } from '../../../components/common/Form';
import RichTextEditor from '../../../components/common/Form/RichTextEditor/RichTextEditor';
import Button from '../../../components/common/Button';
import WizardHeader from '../../../components/WizardHeader';
import MediaPreview from '../../../components/MediaPreview';
import MediaLibraryModal from '../../../components/MediaLibraryModal';
import imageDisplay from '../../../assets/images/ImageDisplay.png';
import { useMediaLibraryContext } from '../../../contexts/MediaLibraryContext';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { createProfilePage, editProfilePage, getProfilePage } from '../../../api/profilePages';
import { createProfileSection, editProfileSection, linkMediasToProfileSection } from '../../../api/profileSections';
import { getRestaurantDetails } from '../../../api/restaurant';
import { PAGE_ADD_PAGE, PAGE_CUSTOM_PAGE, PAGE_PAGES } from '../../../constants/UriConstants';
import { ProfilePageInterface } from '../../../types/ProfilePageInterface';
import {
  CreateProfileSectionRequestInterface,
  CreateProfileSectionResponseInterface,
  ProfileSectionInterface
} from '../../../types/ProfileSectionInterface';
import {
  MediaLibraryInterface,
  MenuItemMediaInterface,
  ProfileSectionMediaInterface
} from '../../../types/MediaInterface';
import { initialRichText, richTextFields, richTextSchema } from '../../../utils/richText';
import {
  CustomPageTemplate,
  customPageTemplateOf,
  customPageUrlPath,
  isBuiltInPageKey,
  isCustomPageTemplate
} from '../../../utils/customPages';

interface SectionValues {
  sectionID?: number;
  text: string;
  photo: MenuItemMediaInterface | null;
}

interface CustomPageValues {
  title: string;
  coverPhoto: MenuItemMediaInterface | null;
  sections: SectionValues[];
}

const emptySection = (): SectionValues => ({ text: '', photo: null });

const EMPTY_VALUES: CustomPageValues = { title: '', coverPhoto: null, sections: [emptySection()] };

const COPY_SECTION_NAME = 'Copy Section';
const COVER_PHOTO_SECTION_NAME = 'Cover Photo Section';
const sectionName = (index: number) => `Section ${index + 1}`;

const schemaFor = (template: CustomPageTemplate) =>
  object({
    title: string()
      .trim()
      .required('Page title is required.')
      .test(
        'has-url-path',
        'Page title needs at least one letter or number.',
        (value) => !value || !!customPageUrlPath(value)
      )
      .test('not-built-in', 'That title is used by a built-in page.', (value) => !value || !isBuiltInPageKey(value)),
    coverPhoto: template === 'cover-photo' ? mixed().required('Cover photo is required.') : mixed().nullable(),
    sections: array().of(
      object({
        text: richTextSchema('Page text is required.'),
        photo: template === 'sections' ? mixed().required('Section photo is required.') : mixed().nullable()
      })
    )
  });

const toMedia = (media?: ProfileSectionMediaInterface): MenuItemMediaInterface | null =>
  media ? { mediaID: media.mediaID, mediaURL: media.mediaUrl, type: media.type ?? 'image' } : null;

const valuesFromPage = (page: ProfilePageInterface, template: CustomPageTemplate): CustomPageValues => {
  const bySection = (name: string) => page.profileSections.find((section) => section.template === name);
  if (template === 'sections') {
    return {
      title: page.name,
      coverPhoto: null,
      sections: page.profileSections
        .filter((section) => section.template === 'media_copy')
        .map((section) => ({
          sectionID: section.sectionID,
          text: initialRichText(section.contentHtml, section.content),
          photo: toMedia(section.media?.[0])
        }))
    };
  }
  const copy = bySection('copy');
  return {
    title: page.name,
    coverPhoto: toMedia(bySection('media_gallery')?.media?.[0]),
    sections: [{ sectionID: copy?.sectionID, text: initialRichText(copy?.contentHtml, copy?.content), photo: null }]
  };
};

const copySectionFields = (title: string, text: string) => ({
  title,
  ...richTextFields(text),
  template: 'copy',
  isHidden: false
});

const mediaCopySectionFields = (text: string) => ({ ...richTextFields(text), template: 'media_copy', isHidden: false });

const sectionsToCreate = (
  template: CustomPageTemplate,
  values: CustomPageValues,
  title: string
): Omit<CreateProfileSectionRequestInterface, 'pageID'>[] => {
  if (template === 'sections') {
    return values.sections.map((section, index) => ({
      name: sectionName(index),
      ...mediaCopySectionFields(section.text)
    }));
  }
  const copy = { name: COPY_SECTION_NAME, ...copySectionFields(title, values.sections[0].text) };
  return template === 'cover-photo'
    ? [{ name: COVER_PHOTO_SECTION_NAME, template: 'media_gallery', isHidden: false }, copy]
    : [copy];
};

const linkPhoto = (sectionID: number, photo: MenuItemMediaInterface | null) =>
  linkMediasToProfileSection({ sectionID, mediaIDs: photo ? [photo.mediaID] : [] });

const createPage = async (template: CustomPageTemplate, values: CustomPageValues, title: string) => {
  const created = await createProfilePage({
    name: title,
    navLink: title,
    urlPath: customPageUrlPath(title),
    isHidden: false,
    profileSections: sectionsToCreate(template, values, title)
  });
  const createdSection = (name: string) =>
    created.profileSections.find((section: CreateProfileSectionResponseInterface) => section.name === name);

  if (template === 'cover-photo') {
    await linkPhoto(createdSection(COVER_PHOTO_SECTION_NAME).sectionID, values.coverPhoto);
  } else if (template === 'sections') {
    await values.sections.reduce(async (previous, section, index) => {
      await previous;
      await linkPhoto(createdSection(sectionName(index)).sectionID, section.photo);
    }, Promise.resolve());
  }
  return created.pageID;
};

const updatePage = async (
  page: ProfilePageInterface,
  template: CustomPageTemplate,
  values: CustomPageValues,
  title: string
) => {
  await editProfilePage({ pageID: page.pageID, name: title, navLink: title, urlPath: customPageUrlPath(title) });

  const existing = (name: string): ProfileSectionInterface | undefined =>
    page.profileSections.find((section) => section.template === name);
  const createSection = (fields: Omit<CreateProfileSectionRequestInterface, 'pageID'>) =>
    createProfileSection({ pageID: page.pageID, ...fields });

  if (template === 'sections') {
    await values.sections.reduce(async (previous, section, index) => {
      await previous;
      let { sectionID } = section;
      if (sectionID) {
        await editProfileSection({ sectionID, ...mediaCopySectionFields(section.text) });
      } else {
        sectionID = (await createSection({ name: sectionName(index), ...mediaCopySectionFields(section.text) }))
          .sectionID;
      }
      await linkPhoto(sectionID, section.photo);
    }, Promise.resolve());
    return;
  }

  const copyID = existing('copy')?.sectionID;
  const copyFields = copySectionFields(title, values.sections[0].text);
  if (copyID) {
    await editProfileSection({ sectionID: copyID, ...copyFields });
  } else {
    await createSection({ name: COPY_SECTION_NAME, ...copyFields });
  }

  if (template === 'cover-photo') {
    const coverID =
      existing('media_gallery')?.sectionID ??
      (await createSection({ name: COVER_PHOTO_SECTION_NAME, template: 'media_gallery', isHidden: false })).sectionID;
    await linkPhoto(coverID, values.coverPhoto);
  }
};

interface PhotoPickerProps {
  label: string;
  photo: MenuItemMediaInterface | null;
  onChange: (photo: MenuItemMediaInterface | null) => void;
  error?: string;
}

const PhotoPicker = ({ label, photo, onChange, error }: PhotoPickerProps) => {
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const handleSelect = (media: MediaLibraryInterface[]) =>
    onChange(media[0] ? { mediaID: media[0].mediaID, mediaURL: media[0].mediaUrl, type: 'image' } : null);

  return (
    <div className="custom-page-photo">
      <Label label={label} />
      <div className="custom-page-photo-box">
        {photo ? (
          <MediaPreview
            media={[photo]}
            onUpdate={(remaining: MenuItemMediaInterface[]) => onChange(remaining[0] ?? null)}
          />
        ) : (
          <Button className="custom-page-photo-select" onClick={() => setIsLibraryOpen(true)}>
            <img src={imageDisplay} alt="" />
            <span>Select Photo</span>
          </Button>
        )}
      </div>
      {error && <ErrorText error={error} />}
      {isLibraryOpen && (
        <MediaLibraryModal
          selectedMediaIDs={photo ? [photo.mediaID] : []}
          onSelect={handleSelect}
          onRemove={() => onChange(null)}
          onClose={() => setIsLibraryOpen(false)}
          mediaTypeOverride="image"
        />
      )}
    </div>
  );
};

const CustomPageEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { template: templateParam, pageID: pageIDParam } = useParams();
  const pageID = pageIDParam ? Number(pageIDParam) : undefined;
  const addSectionAfterLoad = (location.state as { addSection?: boolean } | null)?.addSection;

  const { loadMedia } = useMediaLibraryContext();
  const { currentRestaurant, updateRestaurant } = useRestaurantContext();
  const restaurantIDRef = useRef(currentRestaurant?.restaurantID);

  const [page, setPage] = useState<ProfilePageInterface>();
  const [template, setTemplate] = useState<CustomPageTemplate | undefined>(
    isCustomPageTemplate(templateParam) ? templateParam : undefined
  );
  const [initialValues, setInitialValues] = useState<CustomPageValues>(EMPTY_VALUES);
  const [isLoading, setIsLoading] = useState(pageID != null);
  const [isSaving, setIsSaving] = useState(false);
  const addSectionRef = useRef(false);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    const restaurantID = currentRestaurant?.restaurantID;
    if (restaurantID == null) return;
    if (restaurantIDRef.current == null) {
      restaurantIDRef.current = restaurantID;
    } else if (restaurantIDRef.current !== restaurantID) {
      navigate(`/${PAGE_PAGES}`);
    }
  }, [currentRestaurant?.restaurantID, navigate]);

  useEffect(() => {
    if (pageID == null) return undefined;
    let canceled = false;
    getProfilePage(pageID)
      .then((data) => {
        if (canceled) return;
        const pageTemplate = customPageTemplateOf(data);
        const values = valuesFromPage(data, pageTemplate);
        if (addSectionAfterLoad || !values.sections.length) values.sections.push(emptySection());
        setPage(data);
        setTemplate(pageTemplate);
        setInitialValues(values);
      })
      .catch(() => {
        if (!canceled) toast.error('Could not load this page.', { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [pageID, location.key, addSectionAfterLoad]);

  const save = async (values: CustomPageValues) => {
    const addSection = addSectionRef.current;
    addSectionRef.current = false;
    const title = values.title.trim();
    setIsSaving(true);
    try {
      let savedPageID: number;
      if (page) {
        await updatePage(page, template, values, title);
        savedPageID = page.pageID;
      } else {
        savedPageID = await createPage(template, values, title);
      }
      updateRestaurant(await getRestaurantDetails());
      toast.success('Page saved.', { position: toast.POSITION.TOP_RIGHT });
      if (addSection) {
        navigate(`/${PAGE_CUSTOM_PAGE}/${savedPageID}`, { replace: true, state: { addSection: true } });
      } else {
        navigate(`/${PAGE_PAGES}`);
      }
    } catch (error) {
      toast.error(
        error?.response?.status === 409
          ? 'A page with that title already exists. Please choose a different title.'
          : 'Error saving page. If this issue keeps occurring please reach out to the TapTab team.',
        { position: toast.POSITION.TOP_RIGHT }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const formik = useFormik<CustomPageValues>({
    initialValues,
    validationSchema: template ? schemaFor(template) : undefined,
    enableReinitialize: true,
    onSubmit: save
  });

  if (pageID == null && !template) return <Navigate to={`/${PAGE_ADD_PAGE}`} replace />;
  if (isLoading) return <div>Loading...</div>;
  if (!template) return null;

  const sectionError = (index: number, field: keyof SectionValues): string | undefined => {
    const error = formik.errors.sections?.[index] as FormikErrors<SectionValues> | undefined;
    const touched = formik.touched.sections?.[index]?.[field];
    return touched ? (error?.[field] as string | undefined) : undefined;
  };

  const textEditor = (index: number, label: string) => (
    <RichTextEditor
      name={`sections.${index}.text`}
      label={label}
      className="custom-page-text"
      onChange={(html) => formik.setFieldValue(`sections.${index}.text`, html)}
      onBlur={() => formik.setFieldTouched(`sections.${index}.text`)}
      value={formik.values.sections[index].text}
      touched={formik.touched.sections?.[index]?.text}
      error={sectionError(index, 'text')}
    />
  );

  return (
    <div className="custom-page-container">
      <WizardHeader className="custom-page-header">
        <div className="custom-page-header-content">
          <h1 className="custom-page-header-title">{page ? 'EDIT PAGE' : 'NEW PAGE'}</h1>
        </div>
      </WizardHeader>
      <form className="custom-page-content" onSubmit={formik.handleSubmit}>
        <Input
          name="title"
          label="PAGE TITLE"
          className="custom-page-title-input"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.title}
          touched={formik.touched.title}
          error={formik.errors.title}
        />
        {template === 'cover-photo' && (
          <PhotoPicker
            label="PAGE COVER PHOTO"
            photo={formik.values.coverPhoto}
            onChange={(photo) => {
              formik.setFieldValue('coverPhoto', photo);
              formik.setFieldTouched('coverPhoto', true, false);
            }}
            error={formik.touched.coverPhoto ? (formik.errors.coverPhoto as string) : undefined}
          />
        )}
        {template === 'sections'
          ? formik.values.sections.map((section, index) => (
              <div className="custom-page-section" key={section.sectionID ?? `new-${index}`}>
                <PhotoPicker
                  label={`SECTION ${index + 1} COVER PHOTO`}
                  photo={section.photo}
                  onChange={(photo) => {
                    formik.setFieldValue(`sections.${index}.photo`, photo);
                    formik.setFieldTouched(`sections.${index}.photo`, true, false);
                  }}
                  error={sectionError(index, 'photo')}
                />
                {textEditor(index, `SECTION ${index + 1} TEXT`)}
              </div>
            ))
          : textEditor(0, 'PAGE TEXT')}
        <div className="custom-page-actions">
          <Button className="custom-page-cancel-button" onClick={() => navigate(`/${PAGE_PAGES}`)}>
            CANCEL
          </Button>
          {template === 'sections' && (
            <Button
              className="custom-page-add-section-button"
              isDisabled={isSaving}
              onClick={() => {
                addSectionRef.current = true;
                formik.submitForm();
              }}
            >
              SAVE & ADD SECTION
            </Button>
          )}
          <Button className="custom-page-save-button" submit isDisabled={isSaving}>
            {isSaving ? 'SAVING...' : 'SAVE'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomPageEditor;
